import PropTypes from 'prop-types';
import React from 'react';

import { CircleLoader } from 'common-src/components/base';
import { AthenaDepartment } from 'common-src/models/PatientIntegrationsAthena';
import { ColorsNew } from 'common-src/styles';

import { VISIT_TYPES } from '../../constants';
import { dayName, daysUntil, getAppointmentReadinessStatus, monthDay } from '../../helpers';
import AppointmentCard from '../AppointmentCard';
import ReadinessDetail from '../ReadinessDetail';
import TaskDetail from '../TaskDetail';
import styles from './ReadinessView.module.scss';

const ALL_DEPARTMENTS_KEY = 'all';
const defaultAppointmentTaskLookupKey = (appointment) => `${appointment?.id || ''}`.trim();
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseReadinessCalendarDate = (value) => {
  const normalizedValue = `${value ?? ''}`.trim();
  if (!normalizedValue) return null;

  const dateOnlyMatch = normalizedValue.match(DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsedDate = new Date(normalizedValue);
  return Number.isFinite(parsedDate.getTime()) ? parsedDate : null;
};

const ReadinessView = ({
  isLoading,
  apptsByDate,
  selectedAppt,
  taskFromReadiness,
  appointmentTasksById,
  getAppointmentTaskLookupKey = defaultAppointmentTaskLookupKey,
  setSelectedAppt,
  setTaskFromReadiness,
  onMarkComplete,
  markingTaskId,
  onCancelTask,
  cancelingTaskId,
  onReassignTask,
  reassigningTaskId,
  onFollowTask,
  followingTaskId,
  onUnfollowTask,
  unfollowingTaskId,
  onAssignToMe,
  assigningTaskId,
  onUnassignTask,
  unassigningTaskId,
  onBlockTask,
  blockingTaskId,
  onUnblockTask,
  unblockingTaskId,
  onEscalateTask,
  escalatingTaskId,
  onDeescalateTask,
  deescalatingTaskId,
  onDelegateSubtasks,
  delegatingSubtasksTaskId,
  onCancelSubtask,
  cancelingSubtaskId,
  currentUserId,
  currentUserName,
  onReplySent,
  delegatedSubtasksByParentId,
}) => {
  const [selectedDepartment, setSelectedDepartment] = React.useState(ALL_DEPARTMENTS_KEY);
  const hasApptsByDateProp = apptsByDate != null;
  const safeApptsByDate = apptsByDate ?? {};
  const flattenedAppointments = React.useMemo(
    () => Object.values(safeApptsByDate).flat(),
    [safeApptsByDate],
  );
  const departmentDefinitions = React.useMemo(
    () =>
      Object.values(AthenaDepartment)
        .filter((department) => department.id !== AthenaDepartment.TestDepartment.id)
        .map((department) => ({
          key: `${department.id}`,
          label: department.value,
        })),
    [],
  );
  const departmentLabelsById = React.useMemo(
    () =>
      departmentDefinitions.reduce(
        (acc, department) => ({ ...acc, [department.key]: department.label }),
        {},
      ),
    [departmentDefinitions],
  );
  const departmentOptions = React.useMemo(
    () => [{ key: ALL_DEPARTMENTS_KEY, label: 'All clinics' }, ...departmentDefinitions],
    [departmentDefinitions],
  );
  const departmentReadinessById = React.useMemo(
    () =>
      departmentDefinitions.reduce((acc, department) => {
        const departmentAppointments = flattenedAppointments.filter(
          (appointment) => `${appointment?.departmentAthenaId ?? ''}`.trim() === department.key,
        );

        if (!departmentAppointments.length) {
          acc[department.key] = 'default';
          return acc;
        }

        const hasRed = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(
              appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
            ) === 'red',
        );
        const hasYellow = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(
              appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
            ) === 'yellow',
        );
        const hasOrange = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(
              appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
            ) === 'orange',
        );

        acc[department.key] = hasRed
          ? 'red'
          : hasYellow
            ? 'yellow'
            : hasOrange
              ? 'orange'
              : 'green';
        return acc;
      }, {}),
    [appointmentTasksById, departmentDefinitions, flattenedAppointments],
  );
  const filteredAppointments = React.useMemo(() => {
    if (selectedDepartment === ALL_DEPARTMENTS_KEY) return flattenedAppointments;

    return flattenedAppointments.filter(
      (appointment) => `${appointment?.departmentAthenaId ?? ''}`.trim() === selectedDepartment,
    );
  }, [flattenedAppointments, selectedDepartment]);
  const filteredApptsByDate = React.useMemo(
    () =>
      filteredAppointments.reduce((acc, appointment) => {
        const dateKey = `${appointment?.departmentLocalDate ?? ''}`.trim();
        if (!dateKey) return acc;
        acc[dateKey] = acc[dateKey] || [];
        acc[dateKey].push(appointment);
        return acc;
      }, {}),
    [filteredAppointments],
  );
  const filteredReadinessStats = React.useMemo(
    () =>
      filteredAppointments.reduce(
        (acc, appointment) => {
          const readinessStatus = getAppointmentReadinessStatus(
            appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
          );

          if (readinessStatus === 'green') acc.green += 1;
          if (readinessStatus === 'orange') acc.orange += 1;
          if (readinessStatus === 'yellow') acc.yellow += 1;
          if (readinessStatus === 'red') acc.red += 1;

          return acc;
        },
        { red: 0, yellow: 0, orange: 0, green: 0 },
      ),
    [appointmentTasksById, filteredAppointments],
  );
  const next14DayAppointments = hasApptsByDateProp
    ? filteredAppointments.filter((appointment) => {
        const dayOffset = appointment?.date ? daysUntil(appointment.date) : null;
        return Number.isFinite(dayOffset) && dayOffset >= 0 && dayOffset <= 14;
      }).length
    : 0;

  React.useEffect(() => {
    const filteredAppointmentIds = new Set(
      filteredAppointments.map((appointment) => `${appointment?.id || ''}`),
    );
    const filteredSourceAppointmentIds = new Set(
      filteredAppointments.map(
        (appointment) => `${appointment?.sourceAppointmentId || appointment?.id || ''}`,
      ),
    );

    if (selectedAppt?.id && !filteredAppointmentIds.has(`${selectedAppt.id}`)) {
      setSelectedAppt(null);
      setTaskFromReadiness(null);
      return;
    }

    if (
      taskFromReadiness?.externalObjectId &&
      !filteredSourceAppointmentIds.has(`${taskFromReadiness.externalObjectId}`)
    ) {
      setTaskFromReadiness(null);
    }
  }, [
    filteredAppointments,
    selectedAppt,
    setSelectedAppt,
    setTaskFromReadiness,
    taskFromReadiness,
  ]);

  const sortedDates = Object.keys(filteredApptsByDate).sort((a, b) => {
    const aTime = parseReadinessCalendarDate(a)?.getTime();
    const bTime = parseReadinessCalendarDate(b)?.getTime();
    const aValid = Number.isFinite(aTime);
    const bValid = Number.isFinite(bTime);
    if (aValid && bValid) return aTime - bTime;
    if (aValid) return -1;
    if (bValid) return 1;
    return a.localeCompare(b);
  });

  const stats = [
    { key: 'upcoming', label: 'Next 14 days', value: next14DayAppointments, sub: 'visits' },
    { key: 'green', label: 'Ready', value: filteredReadinessStats.green, tone: 'green' },
    { key: 'orange', label: 'Canceled', value: filteredReadinessStats.orange, tone: 'orange' },
    { key: 'yellow', label: 'At Risk', value: filteredReadinessStats.yellow, tone: 'yellow' },
    { key: 'red', label: 'Not Ready', value: filteredReadinessStats.red, tone: 'red' },
  ];
  const readinessAppointmentLabel = selectedAppt
    ? `${selectedAppt?.patient?.name || 'Appointment'} · ${
        VISIT_TYPES[selectedAppt?.visitType]?.label || selectedAppt?.visitType || ''
      }`
    : '';
  const readinessStatus = selectedAppt
    ? getAppointmentReadinessStatus(
        appointmentTasksById[getAppointmentTaskLookupKey(selectedAppt)] || [],
      )
    : 'default';

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.departmentTabs} role="tablist" aria-label="Department filters">
          {departmentOptions.map((department) => (
            <button
              key={department.key}
              type="button"
              role="tab"
              aria-selected={selectedDepartment === department.key}
              onClick={() => {
                setSelectedDepartment(department.key);
              }}
              className={`${styles.departmentTab} ${
                selectedDepartment === department.key ? styles.departmentTabActive : ''
              }`}
              data-tone={departmentReadinessById[department.key] || 'default'}
            >
              {department.label}
            </button>
          ))}
        </div>
        <div
          className={styles.toolbarMeta}
        >{`${next14DayAppointments} appointments · next 14 days`}</div>
      </div>

      <div className={styles.statsRow}>
        {stats.map((stat) => (
          <div key={stat.key} className={styles.statCard}>
            <div className={styles.statLabel}>{stat.label}</div>
            <div className={styles.statValueRow}>
              <span
                className={`${styles.statValue} ${
                  stat.tone ? styles[`statValue${stat.tone}`] : ''
                }`}
              >
                {stat.value}
              </span>
              {stat.sub && <span className={styles.statSub}>{stat.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      <div
        className={`${styles.layout} ${
          selectedAppt || taskFromReadiness ? styles.layoutWithDetail : ''
        }`}
      >
        <div className={styles.listColumn}>
          {isLoading && (
            <div className={styles.loaderWrap}>
              <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
            </div>
          )}
          {!isLoading && !sortedDates.length && (
            <div className={styles.emptyFilterState}>
              {selectedDepartment === ALL_DEPARTMENTS_KEY
                ? 'No appointments available.'
                : `No appointments for ${departmentLabelsById[selectedDepartment] || 'this department'}.`}
            </div>
          )}
          {!isLoading &&
            sortedDates.map((date) => {
              const appointments = filteredApptsByDate[date] || [];
              const dateObj = parseReadinessCalendarDate(date);
              const isValidDate = Boolean(dateObj);
              const dayOffset = isValidDate ? daysUntil(date) : null;
              const isToday = dayOffset === 0;
              const dayLabel = isValidDate ? dayName(dateObj) : '—';
              const dayNumber = isValidDate ? dateObj.getDate() : '—';
              const monthLabel = isValidDate ? monthDay(dateObj) : '—';

              return (
                <div key={date} className={styles.dateGroup}>
                  <div className={styles.dateHeader}>
                    <div className={styles.dateLeft}>
                      <div
                        className={`${styles.dateDayName} ${
                          isToday ? styles.dateToday : styles.dateMuted
                        }`}
                      >
                        {dayLabel}
                      </div>
                      <div
                        className={`${styles.dateDayNumber} ${
                          isToday ? styles.dateToday : styles.dateNormal
                        }`}
                      >
                        {dayNumber}
                      </div>
                    </div>
                    <div className={styles.dateDivider} />
                    <span className={styles.dateMeta}>
                      {monthLabel}
                      {isToday ? ' · Today' : ''}
                    </span>
                  </div>
                  <div className={styles.cardsGrid}>
                    {appointments.map((appt) => (
                      <AppointmentCard
                        key={appt.id}
                        appt={appt}
                        isSelected={selectedAppt?.id === appt.id}
                        appointmentTasks={
                          appointmentTasksById[getAppointmentTaskLookupKey(appt)] || []
                        }
                        onClick={() => {
                          setSelectedAppt(appt);
                          setTaskFromReadiness(null);
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
        {(selectedAppt || taskFromReadiness) && (
          <div className={styles.detailPanel}>
            <div className={styles.detailPanelInner}>
              {taskFromReadiness ? (
                <TaskDetail
                  task={taskFromReadiness}
                  cachedDelegatedSubtasks={
                    taskFromReadiness?.parentTaskId
                      ? undefined
                      : delegatedSubtasksByParentId?.[`${taskFromReadiness.id}`] || []
                  }
                  onClose={() => setTaskFromReadiness(null)}
                  onBackToReadiness={() => setTaskFromReadiness(null)}
                  readinessAppointmentLabel={readinessAppointmentLabel}
                  readinessStatus={readinessStatus}
                  onAssignToMe={onAssignToMe}
                  isAssigning={assigningTaskId?.has(taskFromReadiness.id)}
                  onUnassign={onUnassignTask}
                  isUnassigning={unassigningTaskId === taskFromReadiness.id}
                  onMarkComplete={onMarkComplete}
                  isMarking={markingTaskId === taskFromReadiness.id}
                  onCancelTask={onCancelTask}
                  isCanceling={cancelingTaskId === taskFromReadiness.id}
                  onReassign={onReassignTask}
                  isReassigning={reassigningTaskId === taskFromReadiness.id}
                  onFollowTask={onFollowTask}
                  isFollowing={followingTaskId === taskFromReadiness.id}
                  onUnfollowTask={onUnfollowTask}
                  isUnfollowing={unfollowingTaskId === taskFromReadiness.id}
                  onBlockTask={onBlockTask}
                  isBlocking={blockingTaskId === taskFromReadiness.id}
                  onUnblockTask={onUnblockTask}
                  isUnblocking={unblockingTaskId === taskFromReadiness.id}
                  onEscalateTask={onEscalateTask}
                  isEscalating={escalatingTaskId === taskFromReadiness.id}
                  onDeescalateTask={onDeescalateTask}
                  isDeescalating={deescalatingTaskId === taskFromReadiness.id}
                  onDelegateSubtasks={onDelegateSubtasks}
                  isDelegatingSubtasks={delegatingSubtasksTaskId === taskFromReadiness.id}
                  onCancelSubtask={onCancelSubtask}
                  cancelingSubtaskId={cancelingSubtaskId}
                  currentUserId={currentUserId}
                  currentUserName={currentUserName}
                  onReplySent={onReplySent}
                  onSelectTask={setTaskFromReadiness}
                />
              ) : (
                <ReadinessDetail
                  appt={selectedAppt}
                  onClose={() => setSelectedAppt(null)}
                  onSelectTask={setTaskFromReadiness}
                  appointmentTasks={
                    appointmentTasksById[getAppointmentTaskLookupKey(selectedAppt)] || []
                  }
                  delegatedSubtasksByParentId={delegatedSubtasksByParentId}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ReadinessView.propTypes = {
  isLoading: PropTypes.bool,
  apptsByDate: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  ),
  selectedAppt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      name: PropTypes.string,
    }),
    visitType: PropTypes.string,
  }),
  taskFromReadiness: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    externalObjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parentTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  appointmentTasksById: PropTypes.object,
  getAppointmentTaskLookupKey: PropTypes.func,
  setSelectedAppt: PropTypes.func,
  setTaskFromReadiness: PropTypes.func,
  onMarkComplete: PropTypes.func,
  markingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelTask: PropTypes.func,
  cancelingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReassignTask: PropTypes.func,
  reassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFollowTask: PropTypes.func,
  followingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnfollowTask: PropTypes.func,
  unfollowingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAssignToMe: PropTypes.func,
  assigningTaskId: PropTypes.instanceOf(Set),
  onUnassignTask: PropTypes.func,
  unassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlockTask: PropTypes.func,
  blockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnblockTask: PropTypes.func,
  unblockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEscalateTask: PropTypes.func,
  escalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeescalateTask: PropTypes.func,
  deescalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDelegateSubtasks: PropTypes.func,
  delegatingSubtasksTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelSubtask: PropTypes.func,
  cancelingSubtaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  onReplySent: PropTypes.func,
  delegatedSubtasksByParentId: PropTypes.object,
};

export default ReadinessView;
